import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  imageUploaded,
  cropChanged,
  aspectRatioChanged,
  outputFormatChanged,
  qualityChanged,
  cropStarted,
  cropCompleted,
  cropFailed,
  resetCropper,
  clearError,
  validateImageFile,
  getMimeTypeAndQuality,
  generateFileName,
  convertCropToPixels,
  AspectRatio,
  OutputFormat,
  selectState,
  selectAspectRatioValue,
} from "./ImageCropperSlice";
import {
  Box,
  Divider,
  MenuItem,
  Stack,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Slider,
} from "@mui/material";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { CustomButton } from "../../components/custom_button/CustomButton";
import UploadIcon from "@mui/icons-material/Upload";
import CropIcon from "@mui/icons-material/Crop";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { HomeCard } from "../../components/home_card/HomeCard";
import ImageConverterImage from "../../assets/ImageConverter.webp";
import CompressionImage from "../../assets/Compression.webp";
import { useState, useRef, useCallback } from "react";
import ReactCrop, {
  Crop,
  PercentCrop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function ImageCropper() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const aspectRatioValue = useAppSelector(selectAspectRatioValue);
  const { t } = useTranslation();
  const [imgRef, setImgRef] = useState<HTMLImageElement>();
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const hiddenAnchorRef = useRef<HTMLAnchorElement>(null);
  const blobUrlRef = useRef("");

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      const validationError = validateImageFile(file);
      if (validationError) {
        dispatch(cropFailed(validationError));
        return;
      }

      const reader = new FileReader();
      reader.addEventListener("load", () => {
        const img = new Image();
        img.onload = () => {
          dispatch(
            imageUploaded({
              dataUrl: reader.result as string,
              fileName: file.name,
              width: img.width,
              height: img.height,
            })
          );
        };
        img.src = reader.result as string;
      });
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;

      // Wait for the image to be fully loaded
      if (img.complete) {
        setImgRef(img);
        initializeCrop(img);
      } else {
        img.onload = () => {
          setImgRef(img);
          initializeCrop(img);
        };
      }
    },
    [dispatch, state.aspectRatio]
  );

  const initializeCrop = useCallback(
    (img: HTMLImageElement) => {
      // Use the display dimensions for percentage calculations
      const { width, height } = img;

      // Initialize crop based on current aspect ratio
      let newCrop: Crop;

      if (state.aspectRatio === AspectRatio.FREE) {
        newCrop = {
          unit: "%",
          x: 25,
          y: 25,
          width: 50,
          height: 50,
        };
      } else {
        const aspectValue = aspectRatioValue;
        if (aspectValue) {
          // Calculate crop dimensions based on aspect ratio
          const imageAspect = width / height;
          let cropWidth, cropHeight;

          if (aspectValue > imageAspect) {
            // Crop is wider than image - fit to width
            cropWidth = 50;
            cropHeight = (50 / aspectValue) * imageAspect;
          } else {
            // Crop is taller than image - fit to height
            cropHeight = 50;
            cropWidth = (50 * aspectValue) / imageAspect;
          }

          newCrop = {
            unit: "%",
            x: (100 - cropWidth) / 2,
            y: (100 - cropHeight) / 2,
            width: cropWidth,
            height: cropHeight,
          };
        } else {
          newCrop = {
            unit: "%",
            x: 25,
            y: 25,
            width: 50,
            height: 50,
          };
        }
      }

      dispatch(cropChanged(newCrop));
    },
    [dispatch, state.aspectRatio]
  );

  const onCropChange = useCallback(
    (crop: Crop) => {
      dispatch(cropChanged(crop));
    },
    [dispatch]
  );

  const handleAspectRatioChange = (newAspectRatio: AspectRatio) => {
    dispatch(aspectRatioChanged(newAspectRatio));

    if (imgRef) {
      const { width, height } = imgRef;
      let newCrop: Crop;

      if (newAspectRatio === AspectRatio.FREE) {
        // For free aspect ratio, just use the current crop but ensure unit is %
        newCrop = {
          ...state.crop,
          unit: "%",
        };
      } else {
        // For fixed aspect ratios, create a new crop
        // Get aspect ratio value for the new aspect ratio
        let aspectValue: number | undefined;
        switch (newAspectRatio) {
          case AspectRatio.SQUARE:
            aspectValue = 1;
            break;
          case AspectRatio.LANDSCAPE_4_3:
            aspectValue = 4 / 3;
            break;
          case AspectRatio.LANDSCAPE_16_9:
            aspectValue = 16 / 9;
            break;
          case AspectRatio.PORTRAIT_3_4:
            aspectValue = 3 / 4;
            break;
          case AspectRatio.PORTRAIT_9_16:
            aspectValue = 9 / 16;
            break;
          default:
            aspectValue = undefined;
        }
        
        if (aspectValue) {
          // Calculate crop dimensions based on aspect ratio
          const imageAspect = width / height;
          let cropWidth, cropHeight;

          if (aspectValue > imageAspect) {
            // Crop is wider than image - fit to width
            cropWidth = 50;
            cropHeight = (50 / aspectValue) * imageAspect;
          } else {
            // Crop is taller than image - fit to height
            cropHeight = 50;
            cropWidth = (50 * aspectValue) / imageAspect;
          }

          newCrop = {
            unit: "%",
            x: (100 - cropWidth) / 2,
            y: (100 - cropHeight) / 2,
            width: cropWidth,
            height: cropHeight,
          };
        } else {
          newCrop = {
            ...state.crop,
            unit: "%",
          };
        }
      }

      dispatch(cropChanged(newCrop));
    }
  };

  const generateCrop = useCallback(async () => {
    if (
      !imgRef ||
      !previewCanvasRef.current ||
      !state.crop.width ||
      !state.crop.height
    ) {
      return;
    }

    dispatch(cropStarted());

    try {
      const canvas = previewCanvasRef.current;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        throw new Error("No 2d context");
      }

      // Ensure the image is fully loaded
      if (!imgRef.complete) {
        throw new Error("Image not fully loaded");
      }

      // Convert crop coordinates to pixel coordinates
      const pixelCoords = convertCropToPixels(state.crop, imgRef.naturalWidth, imgRef.naturalHeight, imgRef.width, imgRef.height);
      const pixelCrop: PixelCrop = {
        unit: "px",
        ...pixelCoords,
      };
      
      // Validate crop dimensions
      if (pixelCrop.width <= 0 || pixelCrop.height <= 0) {
        throw new Error("Invalid crop dimensions");
      }

      // Set canvas size to cropped dimensions
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      // Clear the canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw the cropped image
      ctx.drawImage(
        imgRef,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      // Convert to data URL with specified format and quality
      const { mimeType, quality } = getMimeTypeAndQuality(state.outputFormat, state.quality);
      const dataUrl = canvas.toDataURL(mimeType, quality);

      // Validate the data URL
      if (!dataUrl || dataUrl === "data:,") {
        throw new Error("Failed to generate image data");
      }

      // Download the cropped image immediately
      if (hiddenAnchorRef.current) {
        // Revoke the previous blob URL to avoid memory leaks
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }

        // Convert data URL to blob for download
        const response = await fetch(dataUrl);
        const blob = await response.blob();

        // Validate blob size
        if (blob.size === 0) {
          throw new Error("Generated image is empty");
        }

        blobUrlRef.current = URL.createObjectURL(blob);

        const fileName = generateFileName(state.originalFileName, state.outputFormat);

        hiddenAnchorRef.current.href = blobUrlRef.current;
        hiddenAnchorRef.current.download = fileName;
        hiddenAnchorRef.current.click();
      }

      dispatch(
        cropCompleted({
          dataUrl,
          width: Math.round(pixelCrop.width),
          height: Math.round(pixelCrop.height),
        })
      );
    } catch (error) {
      console.error("Crop error:", error);
      dispatch(
        cropFailed(
          error instanceof Error ? error.message : "Failed to crop image"
        )
      );
    }
  }, [
    imgRef,
    state.crop,
    state.outputFormat,
    state.quality,
    state.originalFileName,
    dispatch,
  ]);

  const handleReset = () => {
    dispatch(resetCropper());
    setImgRef(undefined);

    // Clean up blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = "";
    }
  };

  return (
    <SideMenuPageContainer
      pageTitle={t("imageCropper.title")}
      pageSeoTitle={t("imageCropper.seoTitle")}
      pageDescription={t("imageCropper.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 900, lg: 1200 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("imageCropper.mainHeading")}
            </Typography>
          </Grid2>

          {/* Upload Section */}
          {!state.isImageLoaded && (
            <Grid2 xs={12}>
              <Paper
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: "2px dashed #aedff2",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "#FFD9A8",
                  },
                }}
                onClick={() => document.getElementById("file-input")?.click()}
              >
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  onChange={onSelectFile}
                  style={{ display: "none" }}
                />
                <Stack alignItems="center" spacing={2}>
                  <UploadIcon sx={{ fontSize: 64, color: "#aedff2" }} />
                  <Typography variant="h4" color="secondary">{t("imageCropper.uploadTitle")}</Typography>
                  <Typography variant="body1" color="info">
                    {t("imageCropper.uploadDescription")}
                  </Typography>
                </Stack>
              </Paper>
            </Grid2>
          )}

          {/* Controls Section */}
          {state.isImageLoaded && (
            <>
              <Grid2 xs={12}>
                <CustomSelect
                  label={t("imageCropper.aspectRatio")}
                  value={state.aspectRatio}
                  onChange={(e) =>
                    handleAspectRatioChange(e.target.value as AspectRatio)
                  }
                >
                  <MenuItem value={AspectRatio.FREE}>{t("imageCropper.free")}</MenuItem>
                  <MenuItem value={AspectRatio.SQUARE}>{t("imageCropper.square")}</MenuItem>
                  <MenuItem value={AspectRatio.LANDSCAPE_4_3}>
                    {t("imageCropper.landscape43")}
                  </MenuItem>
                  <MenuItem value={AspectRatio.LANDSCAPE_16_9}>
                    {t("imageCropper.widescreen")}
                  </MenuItem>
                  <MenuItem value={AspectRatio.PORTRAIT_3_4}>
                    {t("imageCropper.portrait34")}
                  </MenuItem>
                  <MenuItem value={AspectRatio.PORTRAIT_9_16}>
                    {t("imageCropper.mobile")}
                  </MenuItem>
                </CustomSelect>
              </Grid2>
              <Grid2 xs={12}>
                <CustomSelect
                  label={t("imageCropper.outputFormat")}
                  value={state.outputFormat}
                  onChange={(e) =>
                    dispatch(
                      outputFormatChanged(e.target.value as OutputFormat)
                    )
                  }
                >
                  <MenuItem value={OutputFormat.PNG}>PNG</MenuItem>
                  <MenuItem value={OutputFormat.JPG}>JPG</MenuItem>
                  <MenuItem value={OutputFormat.WEBP}>WEBP</MenuItem>
                </CustomSelect>

                {(state.outputFormat === OutputFormat.JPG ||
                  state.outputFormat === OutputFormat.WEBP) && (
                  <Box sx={{ width: 200 }}>
                    <Typography variant="body2" gutterBottom>
                      {t("imageCropper.quality")}: {Math.round(state.quality * 100)}%
                    </Typography>
                    <Slider
                      value={state.quality}
                      onChange={(_, value) =>
                        dispatch(qualityChanged(value as number))
                      }
                      min={0.1}
                      max={1.0}
                      step={0.1}
                      size="small"
                    />
                  </Box>
                )}

                <Tooltip title={t("imageCropper.reset")}>
                  <IconButton onClick={handleReset} color="secondary">
                    <RestartAltIcon />
                  </IconButton>
                </Tooltip>
              </Grid2>

              {/* Error Display */}
              {state.error && (
                <Grid2 xs={12}>
                  <Alert
                    severity="error"
                    onClose={() => dispatch(clearError())}
                    sx={{ mb: 2 }}
                  >
                    {state.error}
                  </Alert>
                </Grid2>
              )}

              {/* Crop Section */}
              <Grid2 xs={12}>
                <Stack spacing={2}>
                  <Typography variant="h4">
                    {t("imageCropper.originalImage")} ({state.originalWidth} ×{" "}
                    {state.originalHeight})
                  </Typography>

                  {state.originalImage && (
                    <Box sx={{ maxWidth: "100%", overflow: "auto" }}>
                      <ReactCrop
                        crop={state.crop}
                        onChange={onCropChange}
                                                 aspect={
                           state.aspectRatio === AspectRatio.FREE
                             ? undefined
                             : aspectRatioValue
                         }
                      >
                        <img
                          src={state.originalImage}
                          onLoad={onImageLoad}
                          alt="Crop source"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "400px",
                            objectFit: "contain",
                          }}
                        />
                      </ReactCrop>
                    </Box>
                  )}

                  <Stack direction="row" spacing={2}>
                    <CustomButton
                      icon={<CropIcon sx={{ color: "#FFD9A8" }} />}
                      label={state.isCropping ? t("imageCropper.processing") : t("imageCropper.crop")}
                      onClick={generateCrop}
                      disabled={
                        state.isCropping ||
                        !state.crop.width ||
                        !state.crop.height ||
                        !imgRef?.complete
                      }
                      width={160}
                    />
                  </Stack>
                </Stack>
              </Grid2>
            </>
          )}

          {/* Hidden elements for functionality */}
          <canvas ref={previewCanvasRef} style={{ display: "none" }} />
          <a ref={hiddenAnchorRef} style={{ display: "none" }} />

          <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
            <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
          </Grid2>

          {/* SEO Content */}
          <Grid2 xs={12}>
            <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageCropper.content.freeHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageCropper.content.freeDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageCropper.content.aspectRatiosHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageCropper.content.aspectRatiosDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageCropper.content.outputFormatsHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageCropper.content.outputFormatsDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageCropper.content.securityHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageCropper.content.securityDescription")}
            </Typography>
          </Grid2>

          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("imageCropper.related")}
            </Typography>
          </Grid2>

          <Grid2 xs={6}>
            <HomeCard
              languageResource="imageConverter"
              link="/ImageConverter"
              smallCard={true}
              image={ImageConverterImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="compression"
              link="/Compression"
              smallCard={true}
              image={CompressionImage}
            />
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}
